<template>
  <div class="page-header">
    <h1>管理權限設定</h1>
  </div>

  <!-- 手機版 - 角色卡片列表 -->
  <div class="employee-list-mobile">
    <div class="employee-card" v-for="role in paginatedRoles" :key="role.Id">
      <!-- 角色標題列 -->
      <div class="employee-header">
        <div class="employee-title-section">
          <div class="employee-basic-info">
            <h3 class="employee-name">{{ role.RoleName }}</h3>
          </div>

          <div class="badges-group">
            <span class="status-badge" :class="{ active: role.IsActive }">
              {{ role.IsActive ? "已啟用" : "已停用" }}
            </span>
          </div>
        </div>
      </div>

      <!-- 權限操作按鈕 -->
      <div class="employee-actions">
        <button class="btn-action btn-edit" @click="editRolePermissions(role)">
          編輯權限
        </button>
      </div>
    </div>
  </div>

  <!-- 手機版分頁 -->
  <div class="pagination-container mobile-pagination" v-if="totalPages > 1">
    <button
      class="pagination-btn"
      :disabled="currentPage === 1"
      @click="prevPage"
    >
      ⬅ 上一頁
    </button>

    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>

    <button
      class="pagination-btn"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      下一頁 ➡
    </button>
  </div>

  <!-- 電腦版 - 角色表格 -->
  <div class="employee-table-desktop">
    <div class="table-container">
      <table class="employee-table">
        <thead>
          <tr>
            <th>角色名稱</th>
            <th style="text-align: center">狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="role in paginatedRoles" :key="role.Id" class="table-row">
            <td class="code-cell">
              <span class="code-text">{{ role.RoleName }}</span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="{ active: role.IsActive }">
                {{ role.IsActive ? "已啟用" : "已停用" }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action btn-edit-action"
                  @click="editRolePermissions(role)"
                  title="編輯權限"
                >
                  <img src="@/assets/image/icon/edit.png" class="all-icon" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 電腦版分頁 -->
      <div class="pagination-container" v-if="totalPages > 1">
        <button
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="prevPage"
        >
          ⬅ 上一頁
        </button>

        <span class="pagination-info">
          第 {{ currentPage }} / {{ totalPages }} 頁
        </span>

        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          下一頁 ➡
        </button>
      </div>
    </div>
  </div>

  <!-- 查看權限 Modal -->
  <div class="modal" v-if="showPermissionModal">
    <div class="modal-content modal-large">
      <div class="modal-header">
        <h2>編輯 {{ selectedRole?.RoleName }} 的權限</h2>
        <button class="close-btn" @click="closePermissionModal">×</button>
      </div>

      <form @submit.prevent="saveRolePermissions" class="modal-body">
        <div class="detail-section">
          <h3 class="section-title">分配頁面權限</h3>
          <p class="permission-description">勾選該角色可以訪問的後台管理頁面</p>

          <div class="permission-grid">
            <div
              v-for="page in availablePages"
              :key="page.key"
              class="permission-item"
            >
              <input
                type="checkbox"
                :id="`perm-${selectedRole?.Id}-${page.key}`"
                v-model="formData[pageKeyToFieldMap[page.key]]"
                :disabled="selectedRole?.RoleName === '平台總管'"
              />
              <label :for="`perm-${selectedRole?.Id}-${page.key}`">
                <span>{{ page.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn-cancel"
            @click="closePermissionModal"
          >
            取消
          </button>
          <button type="submit" class="btn-submit">儲存權限</button>
        </div>
      </form>
    </div>
  </div>

  <!-- 新增/編輯角色與權限 Modal -->
  <div class="modal" v-if="showAddModal">
    <div class="modal-content modal-large">
      <div class="modal-header">
        <h2>新增角色與權限</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <form @submit.prevent="saveRole" class="modal-body">
        <!-- 角色基本資訊 -->
        <div class="detail-section">
          <h3 class="section-title">角色選擇</h3>

          <div class="form-group">
            <label class="form-label required">選擇角色</label>
            <select class="form-input" v-model="formData.RoleName" required>
              <option value="">-- 請選擇角色 --</option>
              <option
                v-for="role in fixedRoles"
                :key="role.RoleName"
                :value="role.RoleName"
                :disabled="isRoleAlreadyAdded(role.RoleName)"
              >
                {{ role.RoleName }}
                {{ isRoleAlreadyAdded(role.RoleName) ? "(已存在)" : "" }}
              </option>
            </select>
          </div>
        </div>

        <!-- 權限設定 -->
        <div class="detail-section">
          <h3 class="section-title">分配頁面權限</h3>
          <p class="permission-description">勾選該角色可以訪問的後台管理頁面</p>

          <div class="permission-grid">
            <div
              v-for="page in availablePages"
              :key="page.key"
              class="permission-item"
            >
              <input
                type="checkbox"
                :id="`perm-form-${page.key}`"
                v-model="formData[pageKeyToFieldMap[page.key]]"
                :disabled="formData.RoleName === '平台總管'"
              />
              <label :for="`perm-form-${page.key}`">
                <img :src="page.icon" :alt="page.label" class="perm-icon" />
                <span>{{ page.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeModal">
            取消
          </button>
          <button type="submit" class="btn-submit">新增角色</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script src="@/assets/js/permission.js"></script>
<style scoped src="@/assets/css/account.css"></style>
<style scoped src="@/assets/css/permission.css"></style>
